// The video scrubber's math (jrsm-video.ts): how many cells show as played, how far the keys
// step, the time under a pointer, and what the slider tells assistive tech.

import { clamp } from "@bfly/engine/math";

/** Played and unplayed cells: the field's circles, in the field's font. */
export const PLAYED = "⊚";
export const UNPLAYED = "◯";
export const SCRUB_GLYPHS = [PLAYED, UNPLAYED] as const;

/** How far the arrow keys move, in seconds. */
export const SCRUB_STEP_S = 5;

/** How many of `cells` show as played at `time` of `duration`. */
export function playedCells(time: number, duration: number, cells: number): number {
  if (!(duration > 0)) return 0;
  return Math.round(clamp(time / duration, 0, 1) * cells);
}

/** The time a key moves to, or null for a key the slider ignores. */
export function keyTime(key: string, time: number, duration: number): number | null {
  switch (key) {
    case "ArrowRight":
    case "ArrowUp":
      return clamp(time + SCRUB_STEP_S, 0, duration);
    case "ArrowLeft":
    case "ArrowDown":
      return clamp(time - SCRUB_STEP_S, 0, duration);
    case "Home":
      return 0;
    case "End":
      return duration;
    default:
      return null;
  }
}

/** The time at a pointer's `x` over a scrubber at `left` that is `width` wide. */
export function pointerTime(x: number, left: number, width: number, duration: number): number {
  if (!(width > 0) || !(duration > 0)) return 0;
  return clamp((x - left) / width, 0, 1) * duration;
}

/** A time as "m:ss". */
export function clockTime(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
}

/** The slider's `aria-valuenow`, `aria-valuemax`, and `aria-valuetext` ("0:12 of 0:42"), all
    from the same whole seconds, so a screen reader that uses the numbers hears what the text
    says. Rounding once gave 43 against "0:42". */
export function sliderValues(
  time: number,
  duration: number,
): { now: number; max: number; text: string } {
  const now = Math.max(0, Math.floor(time));
  const max = Math.max(0, Math.floor(duration));
  return { now, max, text: `${clockTime(now)} of ${clockTime(max)}` };
}
